// AssignmentsSection — implements F-002 (eager-parallel credits) plus
// the Edit-on-Completed exception for Admin's End Date edit.

import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignmentsApi,
  creditsApi,
  peopleApi,
} from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import {
  EmptyState,
  ErrorBanner,
  Skeleton,
} from './primitives';
import {
  formatCalendarDate,
  formatCurrencyString,
  formatPercentInt,
  formatPercentOneDecimal,
} from '@/utils/format';
import { ApiError } from '@/types/errors';
import {
  canEditAssignmentEndDateOnCompleted,
  canEditAssignmentsOn,
  isAdmin,
  isOwnerOf,
} from '@/auth/permissions';
import type { Agreement, Assignment, Credit, Person, Principal } from '@/types/domain';
import { ConfirmationDialog } from './ConfirmationDialog';
import { useToast } from './ToastHost';
import { AssignmentEditor } from '@/forms/AssignmentEditor';
import { CreditsSubsection } from './CreditsSubsection';

export interface CreditsByAssignment {
  [assignmentId: string]: {
    credits: Credit[] | null;
    loading: boolean;
    error: ApiError | null;
  };
}

interface Props {
  agreement: Agreement;
  principal: Principal | null;
  onCreditsChange: () => void;
}

export function AssignmentsSection({
  agreement,
  principal,
  onCreditsChange,
}: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const editGate = canEditAssignmentsOn(principal, agreement);
  const completedAdminEndDate = canEditAssignmentEndDateOnCompleted(
    principal,
    agreement,
  );

  const assignmentsQ = useQuery({
    queryKey: qk.agreementAssignments(agreement.id),
    queryFn: () => assignmentsApi.listByAgreement(agreement.id),
  });

  const assignments = assignmentsQ.data?.data ?? [];

  // F-002 eager-parallel credit fetches per assignment.
  const creditQueries = useQueries({
    queries: assignments.map((a) => ({
      queryKey: qk.assignmentCredits(a.id),
      queryFn: () => creditsApi.listByAssignment(a.id),
      enabled: !!a.id,
    })),
  });
  const creditsMap: CreditsByAssignment = useMemo(() => {
    const out: CreditsByAssignment = {};
    assignments.forEach((a, i) => {
      const q = creditQueries[i];
      out[a.id] = {
        credits: q?.data?.data ?? null,
        loading: q?.isPending ?? true,
        error: q?.error instanceof ApiError ? q.error : null,
      };
    });
    return out;
  }, [assignments, creditQueries]);

  // People for displaying assignment.person_id labels.
  const peopleQ = useQuery({
    queryKey: qk.peopleAll(),
    queryFn: () => peopleApi.list({ limit: 100 }),
    staleTime: 60_000,
  });
  const peopleById = useMemo(() => {
    const map = new Map<string, Person>();
    for (const p of peopleQ.data?.data ?? []) map.set(p.id, p);
    return map;
  }, [peopleQ.data]);

  const [editorState, setEditorState] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; assignment: Assignment; endDateOnly: boolean }
    | null
  >(null);
  const [deleting, setDeleting] = useState<Assignment | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => assignmentsApi.delete(id),
  });

  const performDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.id);
      void qc.invalidateQueries({
        queryKey: qk.agreementAssignments(agreement.id),
      });
      toast.show({ severity: 'success', message: 'Assignment deleted.' });
      onCreditsChange();
      setDeleting(null);
    } catch (err) {
      if (err instanceof ApiError) {
        toast.show({ severity: 'error', message: err.message });
      } else {
        toast.show({ severity: 'error', message: "Couldn't delete the assignment." });
      }
      setDeleting(null);
    }
  };

  const showAdd = editGate.rendering === 'visible';
  const isPLOwnerOnly =
    !isAdmin(principal) && principal && isOwnerOf(principal, agreement);
  void isPLOwnerOnly;

  return (
    <section className="section" aria-labelledby="assignments-h2">
      <div className="section-header">
        <h2 id="assignments-h2">Assignments</h2>
        {showAdd ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditorState({ mode: 'create' })}
          >
            Add assignment
          </button>
        ) : editGate.rendering === 'disabled' ? (
          <button
            type="button"
            className="btn btn-secondary"
            disabled
            aria-disabled="true"
            title={editGate.tooltip}
          >
            Add assignment
          </button>
        ) : null}
      </div>

      {assignmentsQ.isPending ? (
        <Skeleton rows={3} height="32px" />
      ) : assignmentsQ.isError ? (
        <ErrorBanner
          message="Couldn't load assignments."
          requestId={
            assignmentsQ.error instanceof ApiError ? assignmentsQ.error.requestId : undefined
          }
          onRetry={() => void assignmentsQ.refetch()}
        />
      ) : assignments.length === 0 ? (
        editGate.rendering === 'visible' ? (
          <EmptyState
            message="No assignments yet. Add the first to enable activation."
            action={
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setEditorState({ mode: 'create' })}
              >
                Add assignment
              </button>
            }
          />
        ) : (
          <EmptyState message="No assignments on this agreement." />
        )
      ) : (
        <table className="data-table">
          <caption className="visually-hidden">
            Assignments on this agreement
          </caption>
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Role</th>
              <th scope="col">Allocation</th>
              <th scope="col">Bill rate</th>
              <th scope="col">Cost rate</th>
              <th scope="col">Start</th>
              <th scope="col">End</th>
              <th scope="col">Sales credit</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((a) => (
              <AssignmentRow
                key={a.id}
                assignment={a}
                agreement={agreement}
                principal={principal}
                creditEntry={creditsMap[a.id]}
                personLabel={
                  peopleById.get(a.person_id)?.name ??
                  peopleById.get(a.person_id)?.email ??
                  a.person_id
                }
                onEdit={() =>
                  setEditorState({
                    mode: 'edit',
                    assignment: a,
                    endDateOnly: completedAdminEndDate.rendering === 'visible',
                  })
                }
                onDelete={() => setDeleting(a)}
                onCreditsChange={onCreditsChange}
              />
            ))}
          </tbody>
        </table>
      )}

      {editorState ? (
        <AssignmentEditor
          open={true}
          agreement={agreement}
          assignment={
            editorState.mode === 'edit' ? editorState.assignment : null
          }
          endDateOnly={
            editorState.mode === 'edit' ? editorState.endDateOnly : false
          }
          principal={principal}
          onClose={() => setEditorState(null)}
        />
      ) : null}

      {deleting ? (
        <ConfirmationDialog
          open={true}
          title="Delete this assignment?"
          body={
            <>
              <p>
                This will delete the assignment and{' '}
                <strong>
                  {(creditsMap[deleting.id]?.credits?.length ?? 0)} credit(s)
                </strong>{' '}
                attached to it. This cannot be undone.
              </p>
              <p>
                Person:{' '}
                {peopleById.get(deleting.person_id)?.name ?? deleting.person_id}
              </p>
            </>
          }
          confirmLabel="Delete assignment"
          confirmIntent="destructive"
          onConfirm={performDelete}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </section>
  );
}

interface RowProps {
  assignment: Assignment;
  agreement: Agreement;
  principal: Principal | null;
  creditEntry: CreditsByAssignment[string] | undefined;
  personLabel: string;
  onEdit: () => void;
  onDelete: () => void;
  onCreditsChange: () => void;
}

function AssignmentRow({
  assignment,
  agreement,
  principal,
  creditEntry,
  personLabel,
  onEdit,
  onDelete,
  onCreditsChange,
}: RowProps) {
  const [creditsExpanded, setCreditsExpanded] = useState(false);
  const editGate = canEditAssignmentsOn(principal, agreement);
  const completedAdminEndDate = canEditAssignmentEndDateOnCompleted(
    principal,
    agreement,
  );

  const credits = creditEntry?.credits ?? null;
  const creditsLoading = creditEntry?.loading ?? false;
  const creditsError = creditEntry?.error ?? null;

  const salesTotal: number | null = (() => {
    if (creditsLoading || creditsError) return null;
    if (!credits) return null;
    let sum = 0;
    for (const c of credits) {
      if (c.credit_type === 'sales') sum += c.percentage;
    }
    return sum;
  })();

  const editVisible = editGate.rendering !== 'hidden';
  const deleteAvailable = editGate.rendering === 'visible';
  const editEndDateAvailable = completedAdminEndDate.rendering === 'visible';

  const subsectionId = `credits-${assignment.id}`;

  return (
    <>
      <tr>
        <th scope="row">{personLabel}</th>
        <td>{assignment.role ?? <span className="muted">—</span>}</td>
        <td>{formatPercentOneDecimal(assignment.allocation_pct)}</td>
        <td>{formatCurrencyString(assignment.bill_rate)}</td>
        <td>{formatCurrencyString(assignment.cost_rate)}</td>
        <td>{formatCalendarDate(assignment.start_date)}</td>
        <td>{formatCalendarDate(assignment.end_date)}</td>
        <td>
          <SalesTotalBadge
            loading={creditsLoading}
            error={!!creditsError}
            total={salesTotal}
          />
        </td>
        <td>
          <span
            className={`badge badge-derived badge-derived-${assignment.derived_status}`}
            aria-label={`Derived status: ${assignment.derived_status}`}
          >
            {assignment.derived_status}
          </span>
        </td>
        <td>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-link"
              onClick={() => setCreditsExpanded((v) => !v)}
              aria-expanded={creditsExpanded ? 'true' : 'false'}
              aria-controls={subsectionId}
            >
              {creditsExpanded ? 'Hide credits' : 'Show credits'}
            </button>
            {editVisible ? (
              editEndDateAvailable ? (
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={onEdit}
                >
                  Edit end date
                </button>
              ) : editGate.rendering === 'visible' ? (
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={onEdit}
                >
                  Edit
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-link"
                  disabled
                  aria-disabled="true"
                  title={editGate.tooltip}
                >
                  Edit
                </button>
              )
            ) : null}
            {deleteAvailable ? (
              <button
                type="button"
                className="btn btn-link"
                onClick={onDelete}
                disabled={!!creditsError}
                aria-disabled={creditsError ? 'true' : undefined}
                title={
                  creditsError
                    ? "Couldn't load credits — refresh to enable delete."
                    : undefined
                }
                style={{ color: 'var(--c-danger)' }}
              >
                Delete
              </button>
            ) : null}
          </div>
        </td>
      </tr>
      {creditsExpanded ? (
        <tr id={subsectionId}>
          <td colSpan={10}>
            <CreditsSubsection
              assignment={assignment}
              agreement={agreement}
              principal={principal}
              credits={credits}
              loading={creditsLoading}
              error={creditsError}
              onCreditsChange={onCreditsChange}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function SalesTotalBadge({
  loading,
  error,
  total,
}: {
  loading: boolean;
  error: boolean;
  total: number | null;
}) {
  if (error) {
    return <span className="muted">Couldn't load</span>;
  }
  if (loading || total === null) {
    return (
      <span aria-label="Loading sales credit total">—</span>
    );
  }
  if (total === 100) {
    return (
      <span
        className="credit-badge credit-badge-complete"
        aria-label={`Sales credit: 100 percent — complete`}
      >
        ✓ {formatPercentInt(total)}
      </span>
    );
  }
  if (total > 100) {
    return (
      <span
        className="credit-badge credit-badge-over"
        aria-label={`Sales credit: ${total} percent — over 100%`}
      >
        ⚠ {formatPercentInt(total)}
      </span>
    );
  }
  return (
    <span
      className="credit-badge credit-badge-incomplete"
      aria-label={`Sales credit: ${total} percent — incomplete`}
    >
      ! {formatPercentInt(total)}
    </span>
  );
}
